import type { ReactNode } from "react"

import MoonBackground from "@/assets/img/today/moon/moon_background_alt.webp"
import TextFrame from "@/assets/img/today/moon/text_frame.webp"
import MoonUp from "@/assets/img/today/moon/moon_up.svg?react"
import MoonDown from "@/assets/img/today/moon/moon_down.svg?react"
import SunUp from "@/assets/img/today/moon/sun_up.svg?react"
import SunDown from "@/assets/img/today/moon/sun_down.svg?react"
import { zodiacIcons, type ZodiacSign } from "@/features/advisors"

import type { MoonHeroExtras } from "../../config/moon-content"

interface MoonHeroProps {
  moonImage: string
  phaseName: string
  description: string
  extras: MoonHeroExtras
}

const ZODIAC_SIGNS = Object.keys(zodiacIcons) as ZodiacSign[]
const ZODIAC_ICON_NODES = Object.fromEntries(
  ZODIAC_SIGNS.map((sign) => {
    const Icon = zodiacIcons[sign]
    return [sign, <Icon key={sign} aria-hidden="true" />]
  }),
) as Record<ZodiacSign, ReactNode>

function getZodiacIcon(signName: string) {
  const sign = ZODIAC_SIGNS.find((item) => item.toLowerCase() === signName.toLowerCase())
  return sign ? ZODIAC_ICON_NODES[sign] : null
}

/** Rich moon hero: day & lunar-sign frames, rise/set times, phase summary. Ported from MoonPhase Hero. */
export function MoonHero({ moonImage, phaseName, description, extras }: MoonHeroProps) {
  const lunarSignIcon = getZodiacIcon(extras.lunarSignName)

  return (
    <div
      className="flex w-full select-none flex-col bg-[length:100%_383px] bg-top bg-no-repeat"
      style={{ backgroundImage: `url(${MoonBackground})` }}
    >
      <div className="flex w-full justify-center px-4 pt-[68px]">
        <Frame
          label="Day & Symbol"
          value={extras.dayNumber}
          name={extras.daySymbolName}
          className="-mr-[17px] pt-3"
        />
        <img src={moonImage} alt={phaseName} className="size-44" />
        <Frame
          label="Lunar Sign"
          value={extras.lunarSignSymbol}
          valueNode={lunarSignIcon}
          name={extras.lunarSignName}
          className="-ml-[17px] pt-3"
          flip
        />
      </div>

      <div className="w-full px-4 pt-8">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-2xl font-medium text-purple-light">{phaseName}</h2>
          <span className="flex items-center justify-center rounded-full bg-purple-light/10 px-3 py-[5px] font-display text-base font-medium text-purple-light backdrop-blur-md">
            {extras.phasePercent}
          </span>
        </div>

        <div className="my-4 flex w-full items-center justify-center gap-2">
          <TimeBox icon={<MoonUp />} value={extras.times.moonUp} />
          <TimeBox icon={<MoonDown />} value={extras.times.moonDown} />
          <TimeBox icon={<SunUp />} value={extras.times.sunUp} />
          <TimeBox icon={<SunDown />} value={extras.times.sunDown} />
        </div>

        <p className="font-body text-base text-purple-light/70">{description}</p>
      </div>
    </div>
  )
}

function Frame({
  label,
  value,
  valueNode,
  name,
  className,
  flip,
}: {
  label: string
  value: string
  valueNode?: ReactNode
  name: string
  className?: string
  flip?: boolean
}) {
  return (
    <div className={`flex flex-col items-center gap-1 ${className ?? ""}`}>
      <span className="rounded-xl bg-purple-light/10 px-2 py-1 font-body text-xs text-purple-light/70 backdrop-blur-md">
        {label}
      </span>
      <div className="relative flex h-[106px] w-[120px] items-center justify-center">
        <img src={TextFrame} alt="" className={flip ? "-scale-x-100" : undefined} />
        <div className="absolute inset-0 flex items-center justify-center">
          {valueNode ? (
            <span className="flex items-center justify-center text-purple-light [&_svg]:size-[54px]">
              {valueNode}
            </span>
          ) : (
            <span className="font-display text-[36px] font-medium text-purple-light">{value}</span>
          )}
        </div>
      </div>
      <span className="font-display text-base font-medium text-purple-light">{name}</span>
    </div>
  )
}

function TimeBox({ icon, value }: { icon: React.ReactNode; value: string }) {
  return (
    <div className="flex w-full flex-col items-center justify-center gap-1 rounded-2xl border border-purple-light/20 py-3 text-center font-body text-sm text-purple-light [&_svg]:h-6 [&_svg]:w-8">
      {icon}
      {value}
    </div>
  )
}
